import { CaretRightFilled } from "@ant-design/icons";
import React from "react";

import { formatMediaDurationFromSeconds } from "@fitness-app/utils/src/media/formatMediaDuration";

interface VideoThumbnailButtonProps {
  onClick: () => void;
  /** Poster frame; without one this falls back to a plain dark tile. */
  thumbUrl?: string | null;
  label: string;
  /** Rendered as a badge in the corner when known. */
  durationSeconds?: number | null;
  width?: number;
  height?: number;
}

/**
 * Opens a video from a poster tile rather than a bare icon (TRE-1784).
 *
 * Square, not the 16:10 a video tile normally gets. These recordings come off a phone in
 * portrait, and a landscape crop of the TRE-1784 sumo squat keeps the torso and cuts the
 * legs — which is the half that says what the exercise is. A square crops far less for a
 * portrait source and still fits a table row; going taller than this starts pushing rows
 * around for no extra detail.
 *
 * The gradient has to be its own element: `bg-neutral-900` is a background *colour* and
 * paints under `backgroundImage`, never over it. It doubles as the no-poster fallback.
 */
const VideoThumbnailButton = ({
  onClick,
  thumbUrl,
  label,
  durationSeconds,
  width = 56,
  height = 56,
}: VideoThumbnailButtonProps): React.ReactElement => (
  <div
    onClick={onClick}
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick();
      }
    }}
    role="button"
    tabIndex={0}
    aria-label={label}
    title={label}
    style={{ width, height, ...(thumbUrl ? { backgroundImage: `url(${thumbUrl})` } : {}) }}
    className="group relative flex shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-md bg-neutral-900 bg-cover bg-center shadow-sm ring-1 ring-black/10 transition-all duration-150 hover:shadow-md hover:ring-2 hover:ring-blue-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
  >
    {/* No dimming layer over the frame. An earlier version washed the whole poster to keep
        the icon readable, which made every preview look muted — the badge carries its own
        contrast instead, so the thumbnail stays as bright as the source. */}
    <span className="relative flex h-6 w-6 items-center justify-center rounded-full bg-black/45 shadow-sm backdrop-blur-[2px] transition-colors duration-150 group-hover:bg-black/65">
      <CaretRightFilled className="text-white" style={{ fontSize: 12, marginLeft: 1 }} />
    </span>

    {typeof durationSeconds === "number" && durationSeconds > 0 ? (
      <span className="pointer-events-none absolute bottom-1 right-1 rounded bg-black/70 px-1 text-[10px] font-medium leading-[14px] text-white">
        {formatMediaDurationFromSeconds(durationSeconds)}
      </span>
    ) : null}
  </div>
);

export default VideoThumbnailButton;
